import React, { useState, useEffect } from 'react';
import { X, Edit, Mail, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface EditUserModalProps {
  user: {
    id: string;
    email: string;
    username: string;
    is_super_admin: boolean;
    roles: Array<{
      id: string;
      name: string;
    }>;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface Role {
  id: string;
  name: string;
  description: string;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const [email, setEmail] = useState(user.email);
  const [username, setUsername] = useState(user.username);
  const [roleId, setRoleId] = useState(user.roles[0]?.id || '');
  const [isSuperAdmin, setIsSuperAdmin] = useState(user.is_super_admin);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('name');

      if (error) throw error;
      setRoles(data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const validateUsername = (username: string): boolean => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username) && !/^[0-9]/.test(username);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateUsername(username)) {
      setError('Username must be 3-20 characters, alphanumeric with underscores, and cannot start with a number');
      return;
    }

    setLoading(true);

    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          username,
          is_super_admin: isSuperAdmin,
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      const { error: deleteRoleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.id);

      if (deleteRoleError) throw deleteRoleError;

      if (roleId) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: user.id,
            role_id: roleId,
          });

        if (roleError) throw roleError;
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error updating user:', err);
      if (err.message?.includes('username')) {
        setError('This username is already taken');
      } else {
        setError(err.message || 'Failed to update user');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Edit className="h-5 w-5 text-blue-900" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-gray-900">{email}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          <Input
            label="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            icon={User}
            required
            fullWidth
            placeholder="johndoe"
          />

          <Select
            label="Role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            required
            fullWidth
          >
            <option value="">Select a role</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="superAdmin"
              checked={isSuperAdmin}
              onChange={(e) => setIsSuperAdmin(e.target.checked)}
              className="h-4 w-4 text-blue-900 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="superAdmin" className="text-sm font-medium text-gray-900">
              Super Administrator
            </label>
          </div>
          <p className="text-xs text-gray-600 -mt-2">
            Super Admins have full access to all features and can manage other users
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={loading}
              fullWidth
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
